/**
 * The model catalog the PICKER renders from — single source of truth shared by
 * the browser and the server.
 *
 * No `@ai-sdk/*`, no `zod`, no server-only import anywhere in this file, so the
 * SPA bundle can pull it in without dragging a provider SDK along.
 *
 * ── Why it lives in `@papan/types` and not in `features/agent` ────────
 *
 * Four packages need this list: `agent` (the picker and the enqueue gate),
 * `billing` (which plans include which models), `orgs` (the platform admin's
 * per-tenant allow-list), and the API. `@papan/billing` deliberately does NOT
 * depend on `@papan/agent` — see the note on `sumBucketUsage` in
 * `features/agent/api/middleware/cost-check.ts` — so a catalog inside `agent`
 * would force exactly the dependency that boundary exists to prevent. It sits
 * here for the same reason `currency.ts` does: shared vocabulary, no feature
 * owns it.
 *
 * ── Why this exists ──────────────────────────────────────────────────
 *
 * Until now `ModelSelector.tsx` carried its OWN copy of this list — ids,
 * labels, costs, `defaultEffort` — with `@sync` comments asking a human to keep
 * it in step with `providers.ts`. Two hand-synced arrays is a drift hazard on
 * its own, and it becomes a blocker the moment the visible set stops being a
 * constant: a client that hardcodes the catalog cannot hide a model per tenant,
 * per plan, or per user. See docs/plans/2026-07-30_MODEL_ACCESS_GOVERNANCE.md.
 *
 * ── What lives here, and what deliberately does not ──────────────────
 *
 * HERE: the facts the picker needs — wire id, short label, which provider tab,
 * relative cost, and the reasoning efforts a model offers.
 *
 * NOT HERE: the billing rate card. `providers.ts` remains the authority for
 * per-token cost, cache pricing, long-context tiers and context limits, because
 * those are what METER a turn and must not acquire a second definition. The
 * costs below are display-only ("is this model pricier than that one") and are
 * pinned to the rate card by agent's `core/__tests__/model-catalog-sync.test.ts`, which fails
 * if the two ever disagree — the check the `@sync` comments used to ask a human
 * to perform.
 *
 * ── Units ────────────────────────────────────────────────────────────
 *
 * Costs here are **per 1M tokens** (what a human reads on a pricing page).
 * `providers.ts` stores **per 1K**. The sync test converts, so the two files can
 * each use the unit that suits them. Do not "fix" one to match the other.
 */

/** Wire form of a model id: `<provider>/<model>`, e.g. `openai/gpt-5.4-mini`. */
export type ModelId = string

export interface ModelCatalogEntry {
  /** The id sent to the server and stored on the session. */
  id: ModelId
  /** Short label for the picker row — the catalog's own name, not the API's. */
  label: string
  /** Which provider tab this renders under. */
  provider: string
  /** Per 1M input tokens, display only. */
  inputCost: number
  /** Per 1M output tokens, display only. */
  outputCost: number
  /**
   * What "Auto" resolves to, or omitted when Auto = the provider's own default.
   * Mirrors `Provider.Model.defaultEffort`; the rubric for CHOOSING it lives
   * there, next to the rate card it trades off against.
   */
  defaultEffort?: 'none' | 'low' | 'medium' | 'high'
}

export interface ProviderTab {
  id: string
  label: string
  /** Tab renders a "Coming soon" placeholder instead of a model list. */
  comingSoon?: boolean
  /** Tab is hidden unless the viewer is mock-eligible (see mock-allowlist.ts). */
  mockOnly?: boolean
}

/** Tab order is display order. Logos stay in the component — this file is data. */
export const PROVIDER_TABS: ProviderTab[] = [
  { id: 'openai', label: 'OpenAI' },
  { id: 'anthropic', label: 'Claude', comingSoon: true },
  { id: 'google', label: 'Gemma', comingSoon: true },
  { id: 'mock', label: 'Mock (test)', mockOnly: true },
]

/**
 * Every model the picker can show, in display order within a provider.
 *
 * A model absent from here is invisible in the UI even if `providers.ts` can
 * run it — which is how the Anthropic and Google entries stay "coming soon"
 * while the server keeps working configs for them.
 */
export const MODEL_CATALOG: ModelCatalogEntry[] = [
  { id: 'openai/gpt-5.6-luna', label: '5.6 Luna', provider: 'openai', inputCost: 0.20, outputCost: 1.20, defaultEffort: 'medium' },
  { id: 'openai/gpt-5.6-terra', label: '5.6 Terra', provider: 'openai', inputCost: 2.50, outputCost: 15.00, defaultEffort: 'medium' },
  { id: 'openai/gpt-5.6-sol', label: '5.6 Sol', provider: 'openai', inputCost: 5.00, outputCost: 30.00, defaultEffort: 'medium' },
  { id: 'openai/gpt-5.5', label: '5.5', provider: 'openai', inputCost: 5.00, outputCost: 30.00, defaultEffort: 'low' },
  { id: 'openai/gpt-5.5-pro', label: '5.5 Pro', provider: 'openai', inputCost: 30.00, outputCost: 180.00 },
  { id: 'openai/gpt-5.4-nano', label: '5.4 Nano', provider: 'openai', inputCost: 0.20, outputCost: 1.25, defaultEffort: 'high' },
  { id: 'openai/gpt-5.4-mini', label: '5.4 Mini', provider: 'openai', inputCost: 0.75, outputCost: 4.50, defaultEffort: 'high' },
  { id: 'openai/gpt-5.4', label: '5.4', provider: 'openai', inputCost: 2.50, outputCost: 15.00, defaultEffort: 'high' },

  // Mock models are free by construction and never reach a provider.
  { id: 'mock/echo', label: 'Echo', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/bash', label: 'Bash (sandbox)', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/bashScoped', label: 'Bash scoped (Angel)', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/stream', label: 'Stream SQL (AG-UI)', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/question', label: 'Question / HITL', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/db-editor', label: 'DB Editor SQL (AG-UI)', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/preview-eval', label: 'Preview Eval (AG-UI)', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/access', label: 'Access tools', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/cred-to-env', label: 'Credential card', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/cred-to-env-otp', label: 'One-time code card', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/tools', label: 'Scripted tool calls', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/error', label: 'Error', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/load-skill', label: 'Load skill', provider: 'mock', inputCost: 0, outputCost: 0 },
  { id: 'mock/subagent-load-skill', label: 'Subagent loads skill', provider: 'mock', inputCost: 0, outputCost: 0 },
]

export const DEFAULT_MODEL: ModelId = 'openai/gpt-5.6-luna'

const BY_ID = new Map(MODEL_CATALOG.map((m) => [m.id, m]))

export function getModelEntry(modelId: ModelId): ModelCatalogEntry | undefined {
  return BY_ID.get(modelId)
}

/** Models under a provider tab, in catalog order. */
export function modelsForProvider(providerId: string): ModelCatalogEntry[] {
  return MODEL_CATALOG.filter((m) => m.provider === providerId)
}

/**
 * Which tab a model belongs to.
 *
 * Prefers the catalog entry and falls back to parsing the id, so a model the
 * server can run but the catalog hasn't listed still lands on a sensible tab
 * rather than vanishing.
 */
export function providerTabOf(modelId: ModelId): string {
  const entry = BY_ID.get(modelId)
  if (entry) return entry.provider
  if (modelId.startsWith('anthropic/')) return 'anthropic'
  if (modelId.startsWith('mock/')) return 'mock'
  if (/gemma|gemini/i.test(modelId)) return 'google'
  return 'openai'
}

/**
 * Reasoning-effort keys a model offers, or `[]` for non-reasoning models.
 *
 * Mirrors the KEY SET of `Provider.reasoningVariants` — that function also
 * builds each key's provider-native options, which only the server needs. Pinned
 * by the sync test.
 */
export function getModelEfforts(modelId: ModelId): string[] {
  // GPT-5.1+ replaced OpenAI's `minimal` tier with `none` (reasoning off); our
  // 5.4+ models are that era, so 'none' is the floor. The *-pro tier only
  // accepts high.
  if (/^openai\/gpt-5/.test(modelId)) return /pro/i.test(modelId) ? ['high'] : ['none', 'low', 'medium', 'high']
  if (/^anthropic\//.test(modelId)) return /haiku/i.test(modelId) ? [] : ['high', 'max']
  return []
}

/** What "Auto" resolves to, or undefined for the provider default. */
export function getModelDefaultEffort(modelId: ModelId): string | undefined {
  return BY_ID.get(modelId)?.defaultEffort
}

/** Picker label, degrading to the id's last segment for an unlisted model. */
export function getShortLabel(modelId: ModelId): string {
  return BY_ID.get(modelId)?.label ?? modelId.split('/').pop() ?? modelId
}


/**
 * A model's DISPLAY price (dollars per 1M tokens) once an operator's override is
 * in force — migration 0167.
 *
 * ── Why this exists rather than the picker reading the catalog ───────────
 *
 * `MODEL_CATALOG`'s `inputCost`/`outputCost` are the SHIPPED defaults, and until
 * overrides existed they were also what every customer paid. They are no longer:
 * an override changes the charged rate and nothing in either static file. A
 * picker that kept showing the catalog number would display one price while
 * metering applied another — the display/enforcement drift this repo keeps
 * getting bitten by, on the one screen where a user chooses how much to spend.
 *
 * The override's unit is USD **microdollars per 1M tokens**, so the conversion is
 * a plain divide by 1e6. It is here, in the dependency-free package, because the
 * agent route, the price book and `model-catalog-sync.test.ts` all need it and
 * none can reach the others.
 */
export interface ModelDisplayOverride {
  inputUsdMicroPer1m: number
  outputUsdMicroPer1m: number
}

export function displayCostFromOverride(
  override: ModelDisplayOverride | null | undefined,
  fallback: { inputCost: number; outputCost: number },
): { inputCost: number; outputCost: number } {
  if (!override) return { inputCost: fallback.inputCost, outputCost: fallback.outputCost }
  return {
    inputCost: override.inputUsdMicroPer1m / 1_000_000,
    outputCost: override.outputUsdMicroPer1m / 1_000_000,
  }
}
